// Button Animation Utilities
export class ButtonAnimations {
  
  // Add to cart button animation with ripple effect
  static animateAddToCart(buttonElement, options = {}) {
    const {
      duration = 600,
      rippleColor = 'rgba(255, 255, 255, 0.6)',
      successColor = 'linear-gradient(135deg, #10b981, #059669)',
      originalBackground = null
    } = options;

    if (!buttonElement) return;

    // Store original styles
    const originalContent = buttonElement.innerHTML;
    const originalBg = originalBackground || buttonElement.style.background || 
                      getComputedStyle(buttonElement).background;

    // Create ripple effect
    this.createRippleEffect(buttonElement, rippleColor);

    // Animate button press
    buttonElement.style.transform = 'scale(0.95)';
    buttonElement.style.transition = 'all 0.1s ease';

    setTimeout(() => {
      // Release press and start success animation
      buttonElement.style.transform = 'scale(1.02)';
      buttonElement.style.background = successColor;
      
      // Change content to success state
      buttonElement.innerHTML = `
        <div class="success-animation">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="check-icon">
            <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
          </svg>
          <span>Added to Cart!</span>
        </div>
      `;

      // Add success animation class
      buttonElement.classList.add('success-state');

      setTimeout(() => {
        // Return to normal state
        buttonElement.style.transform = 'scale(1)';
        buttonElement.style.background = originalBg;
        buttonElement.innerHTML = originalContent;
        buttonElement.classList.remove('success-state');
      }, duration);

    }, 100);
  }

  // Create ripple effect on button click
  static createRippleEffect(element, color = 'rgba(255, 255, 255, 0.6)') {
    const ripple = document.createElement('span');
    const rect = element.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    
    ripple.style.width = ripple.style.height = size + 'px';
    ripple.style.left = (rect.width / 2 - size / 2) + 'px';
    ripple.style.top = (rect.height / 2 - size / 2) + 'px';
    ripple.style.background = color;
    ripple.classList.add('ripple-effect');
    
    // Add ripple styles if not already added
    if (!document.querySelector('#ripple-styles')) {
      const style = document.createElement('style');
      style.id = 'ripple-styles';
      style.textContent = `
        .ripple-effect {
          position: absolute;
          border-radius: 50%;
          transform: scale(0);
          animation: ripple-animation 0.6s linear;
          pointer-events: none;
          z-index: 1;
        }
        
        @keyframes ripple-animation {
          to {
            transform: scale(2);
            opacity: 0;
          }
        }
        
        .success-animation {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          position: relative;
          z-index: 2;
        }
        
        .check-icon {
          animation: check-bounce 0.4s ease-out;
        }
        
        @keyframes check-bounce {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 0;
          }
          50% {
            transform: scale(1.3) rotate(180deg);
            opacity: 1;
          }
          100% {
            transform: scale(1) rotate(360deg);
            opacity: 1;
          }
        }
        
        .success-state {
          position: relative;
          overflow: hidden;
        }
      `;
      document.head.appendChild(style);
    }
    
    // Ensure button has relative positioning for ripple
    const position = getComputedStyle(element).position;
    if (position !== 'relative' && position !== 'absolute') {
      element.style.position = 'relative';
    }
    element.style.overflow = 'hidden';
    
    element.appendChild(ripple);
    
    // Remove ripple after animation
    setTimeout(() => {
      if (ripple.parentNode) {
        ripple.parentNode.removeChild(ripple);
      }
    }, 600);
  }

  // Floating cart icon animation
  static animateFloatingCart(productImage, cartIcon) {
    if (!productImage || !cartIcon) return;

    // Create floating product image
    const floatingImg = productImage.cloneNode(true);
    const productRect = productImage.getBoundingClientRect();
    const cartRect = cartIcon.getBoundingClientRect();
    
    // Style floating image
    floatingImg.style.position = 'fixed';
    floatingImg.style.left = productRect.left + 'px';
    floatingImg.style.top = productRect.top + 'px';
    floatingImg.style.width = productRect.width + 'px';
    floatingImg.style.height = productRect.height + 'px';
    floatingImg.style.zIndex = '10000';
    floatingImg.style.pointerEvents = 'none';
    floatingImg.style.borderRadius = '12px';
    floatingImg.style.transition = 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    floatingImg.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3)';
    
    document.body.appendChild(floatingImg);
    
    // Animate to cart
    setTimeout(() => {
      floatingImg.style.left = cartRect.left + cartRect.width / 2 - 20 + 'px';
      floatingImg.style.top = cartRect.top + cartRect.height / 2 - 20 + 'px';
      floatingImg.style.width = '40px';
      floatingImg.style.height = '40px';
      floatingImg.style.opacity = '0';
      floatingImg.style.transform = 'scale(0.3) rotate(360deg)';
    }, 50);
    
    // Remove floating image
    setTimeout(() => {
      if (floatingImg.parentNode) {
        floatingImg.parentNode.removeChild(floatingImg);
      }
      
      // Animate cart icon
      if (cartIcon) {
        cartIcon.style.transform = 'scale(1.2)';
        cartIcon.style.transition = 'transform 0.2s ease';
        setTimeout(() => {
          cartIcon.style.transform = 'scale(1)';
        }, 200);
      }
    }, 850);
  }

  // Shake animation for error states
  static shakeElement(element) {
    if (!element) return;
    
    element.style.animation = 'shake 0.5s ease-in-out';
    
    // Add shake keyframes if not already added
    if (!document.querySelector('#shake-styles')) {
      const style = document.createElement('style');
      style.id = 'shake-styles';
      style.textContent = `
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
      `;
      document.head.appendChild(style);
    }
    
    setTimeout(() => {
      element.style.animation = '';
    }, 500);
  }

  // Pulse animation for attention
  static pulseElement(element, color = '#0063B8') {
    if (!element) return;
    
    const originalBoxShadow = element.style.boxShadow;
    element.style.animation = `pulse-glow 1s ease-in-out`;
    
    // Add pulse glow keyframes if not already added
    if (!document.querySelector('#pulse-glow-styles')) {
      const style = document.createElement('style');
      style.id = 'pulse-glow-styles';
      style.textContent = `
        @keyframes pulse-glow {
          0%, 100% { 
            box-shadow: 0 0 0 0 ${color}40;
            transform: scale(1);
          }
          50% { 
            box-shadow: 0 0 0 10px ${color}00;
            transform: scale(1.02);
          }
        }
      `;
      document.head.appendChild(style);
    }
    
    setTimeout(() => {
      element.style.animation = '';
      element.style.boxShadow = originalBoxShadow;
    }, 1000);
  }
}

export default ButtonAnimations;